import { useMemo } from 'react';
import { createStreamListener, useStream } from '../socket/use-stream';

/**
 * App-level subscription keeper for the async response-plan result stream.
 *
 * The async response plan returns ONLY over the `responsePlanGenerated` WebSocket stream, and its
 * only *consumer* is the Manage Units panel (`useAsyncResponsePlan`), which mounts and unmounts with
 * the drawer. If that panel were the only listener, the subscribed-stream set would blink in and out
 * with it, and each panel open would race the incoming push (subscribe-before-publish): the server
 * answers in ~1s with no replay/ack, so a broadcast issued before this client rejoins the subscriber
 * set is lost.
 *
 * This component holds the `responsePlanGenerated` subscription open for the whole session, so the
 * stream is in the subscribed set from socket-connect (app boot) — long before any panel opens. When
 * the panel later mounts its own listener, the stream-name set is unchanged, so there is no new
 * subscribe frame and no server round-trip to race; the panel simply joins the already-live
 * client-side fan-out. Repeated open/close is race-free too — the keeper never lets the set drop the
 * stream.
 *
 * It deliberately consumes nothing: `onMessage` is a no-op; the panel is the sole consumer of the
 * payload. Exists only to keep the subscription registered. Mounted flag-gated in `cad-app.tsx`, so
 * `asyncResponsePlansEnabled` false registers zero subscription. CAD-only: First Responder
 * (`fr-app.tsx`) does not open the async Manage Units panel.
 */
export function ResponsePlanGeneratedSubscriptionKeeper() {
    const streamListener = useMemo(
        () =>
            createStreamListener({
                streamNames: ['responsePlanGenerated'] as const,
                onMessage: () => {},
            }),
        []
    );
    useStream(streamListener);
    return null;
}
